import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional custom fallback; receives the reset handler. */
  fallback?: (reset: () => void, error: Error) => ReactNode;
}
interface State { error: Error | null }

/**
 * App-level error boundary. A render error in any route previously blanked the
 * whole portal; this catches it and shows a recoverable, token-themed fallback.
 * Uses no hooks/router (must work even if those are what failed) and no external
 * telemetry — errors are logged to the console for the platform log pipeline to
 * pick up. Scoped boundaries can wrap individual widgets by reusing this.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced to the browser console (and thus to log collection in prod).
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset, error);

    return (
      <div role="alert" className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-danger/10 text-danger" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6">
            <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          </svg>
        </div>
        <div>
          <h1 className="text-lg font-semibold text-fg">Something went wrong</h1>
          <p className="mt-1 max-w-md text-sm text-fg-muted">
            An unexpected error interrupted this view. Your session is still active — try again, or reload the page.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={this.reset}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:opacity-90">
            Try again
          </button>
          <button type="button" onClick={() => window.location.reload()}
            className="rounded-lg border border-border px-4 py-2 text-sm text-fg hover:bg-surface-raised">
            Reload
          </button>
        </div>
      </div>
    );
  }
}
